import { useEffect, useMemo, useState } from "react";
import type { ReactNode } from "react";
import { Button, ConfirmModal, Modal, StatCard } from "@a2z/ui";
import { useTranslations } from "@/lib/i18n/intl";
import { keys } from "@/lib/i18n/portal";
import { useLocale } from "@seller/app/locale";
import { useSellerStoreSlug } from "@/lib/routing/useSellerStoreSlug";
import type { CouponView } from "@/lib/api/types";
import { useCouponsQuery } from "@/modules/coupons/services/useCouponsQuery";
import { useDeleteCouponMutation } from "@/modules/coupons/services/useDeleteCouponMutation";
import { DiscountForm } from "@/modules/coupons/components/DiscountForm";
import { DiscountCard } from "@/modules/coupons/components/DiscountCard";
import {
  couponValueText,
  isCouponActive,
} from "@/modules/coupons/lib/coupon-display";
import { usePageChrome } from "@/shared/layout/PageChromeContext";
import { SegmentedTabs } from "@/shared/components/SegmentedTabs";
import type { SegmentedTab } from "@/shared/components/SegmentedTabs";
import { useIsMobile } from "@/shared/hooks/useIsMobile";
import { MobileHeaderNotificationBell } from "@/modules/notifications/components/MobileHeaderNotificationBell";
import { PageTransition } from "@/shared/components/PageTransition";

type FilterId = "all" | "active" | "inactive";

const CREATE_FORM_ID = "discount-create-form";
const EDIT_FORM_ID = "discount-edit-form";
const PAGE_SIZE = 12;

export function DiscountsPage() {
  const locale = useLocale();
  const storeSlug = useSellerStoreSlug();
  const t = useTranslations();
  const isMobile = useIsMobile();
  const { data, isLoading, error } = useCouponsQuery({ storeSlug });
  const { mutateAsync: deleteDiscount, isPending: isDeleting } =
    useDeleteCouponMutation();
  const { setHero, setHeroAux } = usePageChrome();

  const [isCreateOpen, setIsCreateOpen] = useState(false);
  const [editingDiscount, setEditingDiscount] = useState<CouponView | null>(
    null,
  );
  const [pendingDelete, setPendingDelete] = useState<CouponView | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);
  const [filter, setFilter] = useState<FilterId>("all");
  const [page, setPage] = useState(1);
  const [isFormBusy, setIsFormBusy] = useState(false);

  // The page renders its own title/toolbar inline, so clear the shared chrome.
  useEffect(() => {
    setHero(null);
    setHeroAux(null);
    return () => {
      setHero(null);
      setHeroAux(null);
    };
  }, [setHero, setHeroAux]);

  // Automatic discounts only (trigger `AUTOMATIC`); code coupons live on the
  // Coupons page but share this endpoint.
  const items = useMemo(
    () => (data ?? []).filter((c) => c.trigger === "AUTOMATIC"),
    [data],
  );

  const counts = useMemo(
    () => ({
      all: items.length,
      active: items.filter((c) => isCouponActive(c)).length,
      inactive: items.filter((c) => !isCouponActive(c)).length,
    }),
    [items],
  );
  const totalApplied = useMemo(
    () => items.reduce((sum, c) => sum + c.usesCount, 0),
    [items],
  );

  const visibleItems = useMemo(() => {
    return filter === "all"
      ? items
      : filter === "active"
        ? items.filter((c) => isCouponActive(c))
        : items.filter((c) => !isCouponActive(c));
  }, [items, filter]);

  // Client-side pagination. `page` resets to 1 whenever the filter narrows the
  // list; `safePage` clamps it in case the list shrinks under the current page.
  const pageCount = Math.max(1, Math.ceil(visibleItems.length / PAGE_SIZE));
  const safePage = Math.min(Math.max(1, page), pageCount);
  const pagedItems = useMemo(
    () => visibleItems.slice((safePage - 1) * PAGE_SIZE, safePage * PAGE_SIZE),
    [visibleItems, safePage],
  );

  const changeFilter = (id: FilterId) => {
    setFilter(id);
    setPage(1);
  };

  const pageTitle = t(keys.discounts.pageTitle);
  const createAction = t(keys.discounts.createAction);

  const filterOptions: SegmentedTab<FilterId>[] = [
    { id: "all", label: t(keys.common.all), count: counts.all },
    {
      id: "active",
      label: t(keys.discounts.statusActive),
      count: counts.active,
      dot: "#16A34A",
    },
    {
      id: "inactive",
      label: t(keys.discounts.statusInactive),
      count: counts.inactive,
      dot: "var(--text-disabled)",
    },
  ];

  const handleConfirmDelete = async () => {
    if (!pendingDelete) return;
    setActionError(null);
    try {
      await deleteDiscount({ storeSlug, couponId: pendingDelete.id });
      setPendingDelete(null);
    } catch (caughtError) {
      setActionError(
        caughtError instanceof Error
          ? caughtError.message
          : t(keys.errors.generic),
      );
    }
  };

  const stats = (
    <div className="grid grid-cols-2 gap-3.5 sm:[grid-template-columns:repeat(auto-fit,minmax(170px,1fr))]">
      <StatCard
        icon={<BoxIcon />}
        label={t(keys.discounts.statTotal)}
        value={counts.all}
      />
      <StatCard
        icon={<CheckIcon />}
        label={t(keys.discounts.statusActive)}
        value={counts.active}
      />
      {/* Odd third tile fills the row on the 2-col mobile grid. */}
      <StatCard
        className="col-span-2 sm:col-span-1"
        icon={<BoltIcon />}
        label={t(keys.discounts.statApplied)}
        value={new Intl.NumberFormat(locale === "ar" ? "ar-EG" : "en-US", {
          notation: "compact",
        }).format(totalApplied)}
      />
    </div>
  );

  const filterPills = (
    <SegmentedTabs
      tabs={filterOptions}
      value={filter}
      onChange={changeFilter}
      ariaLabel={t(keys.discounts.navLabel)}
    />
  );

  const content =
    isLoading && !data ? (
      <div className="text-sm text-[color:var(--text-muted)]">
        {t(keys.common.loading)}
      </div>
    ) : error ? (
      <div className="text-sm text-[color:var(--error-500)]">
        {t(keys.discounts.loadError)}
      </div>
    ) : visibleItems.length === 0 ? (
      <div className="flex flex-col items-center gap-2 rounded-[18px] border border-[color:var(--border-light)] bg-[color:var(--surface)] py-14 text-center">
        <p className="text-[15px] font-semibold text-[color:var(--text-primary)]">
          {items.length === 0
            ? t(keys.discounts.emptyTitle)
            : t(keys.common.emptyTitle)}
        </p>
        <p className="px-6 text-[13px] text-[color:var(--text-muted)]">
          {items.length === 0
            ? t(keys.discounts.emptyBody)
            : t(keys.products.filteredEmptyHint)}
        </p>
        {items.length === 0 ? (
          <div className="mt-2">
            <Button
              type="button"
              variant="primary"
              onClick={() => setIsCreateOpen(true)}
            >
              {createAction}
            </Button>
          </div>
        ) : null}
      </div>
    ) : (
      <div className="grid gap-3.5 [grid-template-columns:repeat(auto-fill,minmax(min(100%,250px),1fr))]">
        {pagedItems.map((discount) => (
          <DiscountCard
            key={discount.id}
            locale={locale}
            discount={discount}
            isMutating={isDeleting}
            onEdit={(d) => setEditingDiscount(d)}
            onDelete={(d) => setPendingDelete(d)}
          />
        ))}
      </div>
    );

  const showFooter = !isLoading && !error && visibleItems.length > 0;
  const footer = showFooter ? (
    <>
      {/* Perforated divider */}
      <div
        aria-hidden="true"
        className="my-[18px] h-4"
        style={{
          backgroundImage:
            "repeating-linear-gradient(90deg,var(--border-strong) 0 1px,transparent 1px 9px)",
          WebkitMaskImage:
            "linear-gradient(90deg,transparent,#000 6%,#000 94%,transparent)",
          maskImage:
            "linear-gradient(90deg,transparent,#000 6%,#000 94%,transparent)",
        }}
      />
      <div className="flex items-center justify-between gap-3">
        <span className="text-[13px] text-[color:var(--text-muted)]">
          {t(keys.discounts.showingCount, {
            shown: pagedItems.length,
            total: visibleItems.length,
          })}
        </span>
        {pageCount > 1 ? (
          <div className="flex items-center gap-1">
            <PagerButton
              ariaLabel={t(keys.discounts.pagerPrev)}
              disabled={safePage <= 1}
              onClick={() => setPage((p) => Math.max(1, p - 1))}
            >
              <ChevronLeftIcon />
            </PagerButton>
            {pageWindow(safePage, pageCount).map((item, i) =>
              typeof item === "number" ? (
                <button
                  key={item}
                  type="button"
                  onClick={() => setPage(item)}
                  aria-current={item === safePage ? "page" : undefined}
                  className={[
                    "h-[34px] min-w-[34px] rounded-[9px] px-2 text-[13px] font-semibold transition-colors",
                    item === safePage
                      ? "bg-[color:var(--accent-500)] text-[color:var(--on-accent)]"
                      : "border border-[color:var(--border-light)] bg-[color:var(--surface)] text-[color:var(--text-secondary)] hover:bg-[color:var(--hover)]",
                  ].join(" ")}
                >
                  {item}
                </button>
              ) : (
                <span
                  key={`ellipsis-${i}`}
                  className="px-1 text-[13px] text-[color:var(--text-muted)]"
                >
                  …
                </span>
              ),
            )}
            <PagerButton
              ariaLabel={t(keys.discounts.pagerNext)}
              disabled={safePage >= pageCount}
              onClick={() => setPage((p) => Math.min(pageCount, p + 1))}
            >
              <ChevronRightIcon />
            </PagerButton>
          </div>
        ) : null}
      </div>
    </>
  ) : null;

  return (
    <>
      {/* Phone/tablet: Products/Orders-style title header */}
      {isMobile ? (
        <div
          className="sticky top-0 z-30 border-b border-[color:var(--hair)] bg-[color:var(--canvas)] px-4"
          style={{ paddingTop: "calc(env(safe-area-inset-top, 0px) + 14px)" }}
        >
          {/* Title + add / notification pill */}
          <div className="flex items-center justify-between gap-2 px-1 pb-1 pt-1">
            <h1 className="text-[28px] font-extrabold tracking-[-0.025em] text-[color:var(--text-primary)]">
              {pageTitle}
            </h1>
            <div className="flex items-center gap-1 rounded-full bg-[color:var(--soft2)] p-[5px]">
              <button
                type="button"
                onClick={() => setIsCreateOpen(true)}
                aria-label={createAction}
                className="flex h-[30px] w-[33px] items-center justify-center rounded-full text-[color:var(--text-secondary)] transition-colors hover:bg-[color:var(--soft)]"
              >
                <PlusIcon />
              </button>
              <span className="h-[17px] w-px bg-[color:var(--border-strong)]" />
              <MobileHeaderNotificationBell triggerClassName="relative flex h-[30px] w-[33px] items-center justify-center rounded-full text-[color:var(--text-secondary)] transition-colors hover:bg-[color:var(--soft)]" />
            </div>
          </div>
          {/* Flat status tabs (Orders-style) */}
          <div className="flex items-center gap-1 overflow-x-auto px-1 pb-2 pt-1">
            {filterOptions.map((option) => {
              const on = filter === option.id;
              return (
                <button
                  key={option.id}
                  type="button"
                  onClick={() => changeFilter(option.id)}
                  className="whitespace-nowrap rounded-full px-3 py-[5px] text-[13px] font-semibold transition-colors"
                  style={{
                    background: on ? "var(--soft2)" : "transparent",
                    color: on ? "var(--text-primary)" : "var(--text-muted)",
                  }}
                >
                  {option.label}
                </button>
              );
            })}
          </div>
        </div>
      ) : null}

      <PageTransition className="w-full">
        <div
          className={[
            "flex w-full flex-col gap-4",
            isMobile ? "px-4 pb-24 pt-4" : "px-7 py-6",
          ].join(" ")}
        >
          {/* Desktop title + Add */}
          {!isMobile ? (
            <div className="flex flex-wrap items-end justify-between gap-4">
              <div className="min-w-0">
                <h1 className="text-[24px] font-bold leading-tight tracking-[-0.01em] text-[color:var(--text-primary)]">
                  {pageTitle}
                </h1>
                <p className="mt-1 text-[13.5px] text-[color:var(--text-muted)]">
                  {t(keys.discounts.pageSubtitle)}
                </p>
              </div>
              <button
                type="button"
                onClick={() => setIsCreateOpen(true)}
                className="inline-flex items-center gap-2 rounded-[11px] bg-[color:var(--accent-500)] px-4 py-2.5 text-[13.5px] font-semibold text-[color:var(--on-accent)]"
              >
                <PlusIcon />
                {createAction}
              </button>
            </div>
          ) : null}

          {actionError ? (
            <div
              className="rounded-md border border-[color:var(--error-500)] bg-[color:var(--error-100)] px-4 py-3 text-sm text-[color:var(--error-500)]"
              role="alert"
            >
              {actionError}
            </div>
          ) : null}

          {!isMobile ? stats : null}

          {isMobile ? (
            <>
              {content}
              {footer}
            </>
          ) : (
            <div className="rounded-[20px] border border-[color:var(--border-light)] bg-[color:var(--surface)] p-3 sm:p-4">
              <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
                {filterPills}
              </div>
              {content}
              {footer}
            </div>
          )}
        </div>
      </PageTransition>

      {/* Overlays outside PageTransition (transform re-parents fixed). */}
      <Modal
        isOpen={isCreateOpen}
        onClose={() => setIsCreateOpen(false)}
        icon={<DiscountIcon />}
        title={t(keys.discounts.createTitle)}
        subtitle={t(keys.discounts.createSubtitle)}
        footerActions={
          <>
            <Button
              type="button"
              variant="outline"
              size="md"
              onClick={() => setIsCreateOpen(false)}
            >
              {t(keys.common.cancel)}
            </Button>
            <Button
              type="submit"
              form={CREATE_FORM_ID}
              variant="secondary"
              size="md"
              disabled={isFormBusy}
              leadingIcon={<PlusIcon />}
            >
              {isFormBusy
                ? t(keys.common.loading)
                : t(keys.discounts.createAction)}
            </Button>
          </>
        }
      >
        <DiscountForm
          storeSlug={storeSlug}
          mode="create"
          formId={CREATE_FORM_ID}
          onSubmittingChange={setIsFormBusy}
          onCancel={() => setIsCreateOpen(false)}
          onSuccess={() => setIsCreateOpen(false)}
        />
      </Modal>

      <Modal
        isOpen={editingDiscount !== null}
        onClose={() => setEditingDiscount(null)}
        icon={<DiscountIcon />}
        title={t(keys.discounts.editTitle)}
        footerActions={
          <>
            <Button
              type="button"
              variant="outline"
              size="md"
              onClick={() => setEditingDiscount(null)}
            >
              {t(keys.common.cancel)}
            </Button>
            <Button
              type="submit"
              form={EDIT_FORM_ID}
              variant="secondary"
              size="md"
              disabled={isFormBusy}
            >
              {isFormBusy ? t(keys.common.loading) : t(keys.common.save)}
            </Button>
          </>
        }
      >
        {editingDiscount ? (
          <DiscountForm
            storeSlug={storeSlug}
            mode="edit"
            discount={editingDiscount}
            formId={EDIT_FORM_ID}
            onSubmittingChange={setIsFormBusy}
            onCancel={() => setEditingDiscount(null)}
            onSuccess={() => setEditingDiscount(null)}
          />
        ) : null}
      </Modal>

      <ConfirmModal
        tone="danger"
        isOpen={pendingDelete !== null}
        onClose={() => setPendingDelete(null)}
        onConfirm={() => void handleConfirmDelete()}
        title={t(keys.discounts.deleteConfirmTitle)}
        subtitle={t(keys.discounts.deleteUndoNote)}
        description={t(keys.discounts.deleteDetail)}
        confirmLabel={t(keys.discounts.deleteCta)}
        cancelLabel={t(keys.common.cancel)}
        loadingLabel={t(keys.common.loading)}
        closeLabel={t(keys.common.close)}
        isSubmitting={isDeleting}
        entity={{
          icon: <DiscountIcon />,
          label: t(keys.discounts.discountWord),
          value: pendingDelete
            ? couponValueText(pendingDelete, locale, t)
            : undefined,
        }}
      />
    </>
  );
}

/**
 * Page numbers to render: all of them when ≤7, otherwise a windowed set
 * (first, last, and the current ±1) with "…" gaps.
 */
function pageWindow(current: number, total: number): (number | "…")[] {
  if (total <= 7) {
    return Array.from({ length: total }, (_, i) => i + 1);
  }
  const wanted = [1, total, current, current - 1, current + 1]
    .filter((p) => p >= 1 && p <= total)
    .sort((a, b) => a - b);
  const unique = [...new Set(wanted)];
  const out: (number | "…")[] = [];
  let prev = 0;
  for (const p of unique) {
    if (p - prev > 1) out.push("…");
    out.push(p);
    prev = p;
  }
  return out;
}

function PagerButton({
  ariaLabel,
  disabled,
  onClick,
  children,
}: {
  ariaLabel: string;
  disabled: boolean;
  onClick: () => void;
  children: ReactNode;
}) {
  return (
    <button
      type="button"
      aria-label={ariaLabel}
      disabled={disabled}
      onClick={onClick}
      className="flex h-[34px] w-[34px] items-center justify-center rounded-[9px] border border-[color:var(--border-light)] bg-[color:var(--surface)] text-[color:var(--text-secondary)] transition-colors hover:bg-[color:var(--hover)] disabled:cursor-not-allowed disabled:text-[color:var(--text-disabled)] disabled:hover:bg-[color:var(--surface)]"
    >
      {children}
    </button>
  );
}

function ChevronLeftIcon() {
  return (
    <svg
      width="16"
      height="16"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.8"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <path d="m15 6-6 6 6 6" />
    </svg>
  );
}
function ChevronRightIcon() {
  return (
    <svg
      width="16"
      height="16"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.8"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <path d="m9 6 6 6-6 6" />
    </svg>
  );
}

function PlusIcon({
  size = 15,
  stroke = 2,
}: {
  size?: number;
  stroke?: number;
}) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={stroke}
      strokeLinecap="round"
      aria-hidden="true"
    >
      <path d="M12 5v14M5 12h14" />
    </svg>
  );
}

function BoxIcon() {
  return (
    <svg
      width="13"
      height="13"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.9"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <path d="M21 8 12 3 3 8v8l9 5 9-5V8Z" />
      <path d="m3 8 9 5 9-5" />
    </svg>
  );
}
function CheckIcon() {
  return (
    <svg
      width="13"
      height="13"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <path d="M20 6 9 17l-5-5" />
    </svg>
  );
}
function BoltIcon() {
  return (
    <svg
      width="13"
      height="13"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.8"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <path d="M13 2 3 14h7l-1 8 10-12h-7l1-8Z" />
    </svg>
  );
}

function DiscountIcon(): ReactNode {
  return (
    <svg
      width="18"
      height="18"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.8"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <line x1="19" y1="5" x2="5" y2="19" />
      <circle cx="6.5" cy="6.5" r="2.5" />
      <circle cx="17.5" cy="17.5" r="2.5" />
    </svg>
  );
}
